"use client";

import React, { useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import SectionContainer from "@/components/common/SectionContainer";
const upgradePackages = [{
  id: "linen-package",
  title: "Linen Package",
  description: "Clean, polished look for all essential event tables",
  features: ["Floor-length tablecloths for guest tables, head table, and cake table", "Matching cloth napkins"]
}, {
  id: "linen-dinnerware-package",
  title: "Linen & Dinnerware Package",
  description: "Full table coverings paired with complete guest place settings",
  features: ["Includes all floor-length linens and matching cloth napkins", "Guest dinner plates and dessert plates", "Guest eating utensils (forks, knives, and spoons)"]
}];
export default function Upgrades() {
  const [activeTab, setActiveTab] = useState<'elopement' | 'micro' | 'full'>('micro');
  useEffect(() => {
    const handleTabChange = (event: Event) => {
      const customEvent = event as CustomEvent<{
        tab: 'elopement' | 'micro' | 'full';
      }>;
      if (customEvent.detail?.tab) {
        setActiveTab(customEvent.detail.tab);
      }
    };
    window.addEventListener("pricing-tab-change", handleTabChange);
    return () => {
      window.removeEventListener("pricing-tab-change", handleTabChange);
    };
  }, []);
  const shouldReduceMotion = useReducedMotion();
  if (activeTab === "elopement") return null;
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const headerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.2 : 0.8,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.2 : 0.5,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <section id="optionalupgrades" data-nav="light" className="bg-background text-foreground py-8 md:py-12">
      <SectionContainer>
        <div className="mx-auto max-w-5xl">
          <motion.div initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.3
        }} variants={headerVariants} className="flex flex-col items-center text-center mb-12">
          <span className="text-xs uppercase tracking-widest text-muted-foreground font-default font-semibold mb-3">
            OPTIONAL UPGRADES
          </span>
          <h2 className="text-3xl md:text-4xl font-bold font-default tracking-normal text-foreground">
            Enhance Your Event
          </h2>
          <div className="w-16 mt-4">
            <Separator className="bg-border" />
          </div>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }} className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {upgradePackages.map((pkg, i) => <motion.div key={pkg.id} data-index={i} variants={cardVariants} className="flex">
              <Card className="w-full bg-background border border-border rounded-lg shadow-sm flex flex-col justify-between p-6">
                <div>
                  <CardHeader className="p-0 mb-6">
                    <CardTitle className="text-xl font-bold font-default text-foreground">
                      {pkg.title}
                    </CardTitle>
                    <CardDescription className="text-sm font-default text-muted-foreground mt-1.5">
                      {pkg.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-0">
                    <ul className="space-y-3.5">
                      {pkg.features.map((feature, featureIndex) => <li key={featureIndex} data-index={featureIndex} className="flex items-start gap-3">
                          <div className="mt-0.5 shrink-0 rounded-full border border-border p-0.5">
                            <Check className="size-3.5 text-foreground stroke-[2.5]" />
                          </div>
                          <span className="text-sm font-default text-foreground/90 leading-snug">
                            {feature}
                          </span>
                        </li>)}
                    </ul>
                  </CardContent>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

          <div className="mt-8 text-center">
            <p className="text-xs text-muted-foreground font-default leading-relaxed max-w-2xl mx-auto">
              *8.25% sales tax applies to all add-on packages. Chafing dishes, sternos, and buffet serving utensils are not included.
            </p>
          </div>
        </div>
      </SectionContainer>
    </section>;
}